import { tzOpts } from "@/lib/tz";
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  RefreshCw, TrendingUp, Wallet, CheckCircle2, Clock, Plus, Search,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
} from "lucide-react";

const PAGE_SIZE = 25;

const STATUS_MAP = {
  ATIVA: { label: "Aguardando", cls: "bg-orange-500/10 text-orange-300 border border-orange-500/30" },
  CONCLUIDA: { label: "Pago", cls: "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" },
  REMOVIDA_PELO_USUARIO_RECEBEDOR: { label: "Cancelada", cls: "bg-slate-800 text-slate-400 border border-slate-700" },
  REMOVIDA_PELO_PSP: { label: "Removida", cls: "bg-slate-800 text-slate-400 border border-slate-700" },
};

function Metric({ label, value, icon: Icon, accent }) {
  return (
    <div className="border border-slate-800 rounded-lg bg-slate-900/50 p-6 stagger-in">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
        <Icon className={`w-4 h-4 ${accent || "text-emerald-400"}`} strokeWidth={1.75} />
      </div>
      <div className="mt-3 font-outfit text-3xl font-bold text-white tracking-tight font-mono-jb">
        {value}
      </div>
    </div>
  );
}

export default function Payments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [login, setLogin] = useState("");
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const reqId = useRef(0);
  const load = async (q = search, p = page) => {
    const id = ++reqId.current;
    try {
      const { data } = await api.get("/payments/panel", { params: { search: q, page: p, limit: PAGE_SIZE } });
      if (id === reqId.current) {
        setData(data);
        setPage(data.page || 1);
      }
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  useEffect(() => {
    load("", 1);
    // Auto-refresh so a conversao/status muda sozinha em segundo plano - so
    // enquanto ainda esta na pagina 1 sem busca, pra nao "puxar o tapete" de
    // quem esta navegando ou filtrando uma pagina especifica.
    const t = setInterval(() => {
      if (page === 1 && !search.trim()) load("", 1);
    }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToPage = (p) => load(search, p);

  const reconcile = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/pix/reconcile");
      toast.success(`Sincronizado com a EFI. Consultadas: ${data.checked ?? 0} · Baixadas: ${data.updated ?? 0}`);
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setLoading(false);
    }
  };

  const createCharge = async () => {
    if (!login) return toast.error("Informe o login do cliente");
    setCreating(true);
    try {
      const { data } = await api.post("/pix/charges", { login });
      setDetail(data);
      toast.success("PIX criado");
      load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setCreating(false);
    }
  };

  const totals = data?.totals || {};
  const charges = data?.charges || [];

  return (
    <div className="space-y-8">
      <header className="flex items-end justify-between border-b border-slate-800 pb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">EFI PIX</div>
          <h1 className="font-outfit text-4xl font-bold tracking-tight text-white mt-1">Recebimentos</h1>
          <p className="text-sm text-slate-500 mt-2">
            Cobranças PIX criadas pelo chatbot ou manualmente. Status via webhook da EFI e sincronização automática a cada 60s.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="reconcile-btn"
            onClick={reconcile}
            disabled={loading}
            variant="outline"
            className="border-slate-700 hover:bg-slate-800 hover:text-white"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Reconciliar
          </Button>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <Metric label="Cobranças Criadas" value={totals.charges ?? 0} icon={Wallet} />
        <Metric label="Pagas" value={totals.paid ?? 0} icon={CheckCircle2} />
        <Metric label="Aguardando" value={totals.active ?? 0} icon={Clock} accent="text-orange-400" />
        <Metric
          label="Taxa de Conversão"
          value={`${(totals.conversion_rate ?? 0).toFixed(1)}%`}
          icon={TrendingUp}
        />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card className="bg-slate-900/50 border-slate-800 p-6 lg:col-span-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Novo PIX</div>
          <div className="mt-3 space-y-3">
            <Input
              data-testid="pix-login-input"
              placeholder="login do cliente (mk-auth)"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              className="bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500"
            />
            <Button
              data-testid="pix-create-btn"
              onClick={createCharge}
              disabled={creating}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" /> Gerar cobrança
            </Button>
            <div className="text-xs text-slate-500 leading-relaxed">
              Cria uma cobrança PIX imediata com o valor do próximo boleto em aberto do cliente. O
              valor é sempre lido do mk-auth no servidor.
            </div>
          </div>
        </Card>

        <Card className="bg-slate-900/50 border-slate-800 p-6 lg:col-span-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
              Recebimentos {data && `· ${data.total} no total`}
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="payments-search-input"
                placeholder="Buscar por cliente, login, boleto…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load(search, 1)}
                className="bg-slate-950 border-slate-800 text-white h-9 text-sm w-56 focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500"
              />
              <Button
                data-testid="payments-search-btn"
                size="sm"
                onClick={() => load(search, 1)}
                className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold h-9"
              >
                <Search className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                <tr>
                  <th className="text-left py-2 px-2">Data</th>
                  <th className="text-left py-2 px-2">Cliente</th>
                  <th className="text-left py-2 px-2">Boleto</th>
                  <th className="text-left py-2 px-2">Valor</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th className="text-left py-2 px-2">Pago em</th>
                  <th className="text-left py-2 px-2"></th>
                </tr>
              </thead>
              <tbody data-testid="payments-tbody">
                {charges.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-6 text-center text-slate-500">
                      {search.trim()
                        ? "Nenhuma cobrança encontrada com esse filtro."
                        : "Nenhuma cobrança PIX ainda. Configure a EFI e gere sua primeira cobrança."}
                    </td>
                  </tr>
                )}
                {charges.map((c) => {
                  const st = STATUS_MAP[c.status] || { label: c.status, cls: "bg-slate-800 text-slate-400 border border-slate-700" };
                  return (
                    <tr key={c._id} className="border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors duration-200">
                      <td className="py-2 px-2 font-mono-jb text-xs text-slate-400">
                        {new Date(c.created_at).toLocaleString("pt-BR", tzOpts())}
                      </td>
                      <td className="py-2 px-2 text-white">{c.client_name}</td>
                      <td className="py-2 px-2 font-mono-jb text-slate-400">{c.title_id}</td>
                      <td className="py-2 px-2 font-mono-jb text-emerald-300">R$ {c.amount}</td>
                      <td className="py-2 px-2">
                        <Badge className={st.cls}>{st.label}</Badge>
                      </td>
                      <td className="py-2 px-2 font-mono-jb text-xs text-slate-500">
                        {c.paid_at ? new Date(c.paid_at).toLocaleString("pt-BR", tzOpts()) : "—"}
                      </td>
                      <td className="py-2 px-2">
                        <button
                          onClick={() => setDetail(c)}
                          className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors duration-200"
                        >
                          ver
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {data && data.total > 0 && (
            <div className="flex items-center justify-between pt-4 mt-2 border-t border-slate-800 flex-wrap gap-2">
              <div className="text-xs text-slate-500">
                Página {data.page} de {data.pages} · {data.total} recebimento(s) no total
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid="payments-page-first"
                  onClick={() => goToPage(1)}
                  disabled={data.page === 1}
                  className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                <button
                  data-testid="payments-page-prev"
                  onClick={() => goToPage(Math.max(1, data.page - 1))}
                  disabled={data.page === 1}
                  className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-slate-400 px-2 font-mono-jb">{data.page} / {data.pages}</span>
                <button
                  data-testid="payments-page-next"
                  onClick={() => goToPage(Math.min(data.pages, data.page + 1))}
                  disabled={data.page === data.pages}
                  className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  data-testid="payments-page-last"
                  onClick={() => goToPage(data.pages)}
                  disabled={data.page === data.pages}
                  className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </Card>
      </section>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-outfit">Cobrança PIX</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between border-b border-slate-800 pb-2">
                <span className="text-slate-500">Cliente</span>
                <span className="text-white">{detail.client_name}</span>
              </div>
              <div className="flex justify-between border-b border-slate-800 pb-2">
                <span className="text-slate-500">Valor</span>
                <span className="font-mono-jb text-emerald-300">R$ {detail.amount}</span>
              </div>
              <div className="flex justify-between border-b border-slate-800 pb-2">
                <span className="text-slate-500">TXID</span>
                <span className="font-mono-jb text-slate-300 text-xs truncate max-w-[60%]">{detail.txid}</span>
              </div>
              {detail.qr_image_data_uri && (
                <img
                  src={detail.qr_image_data_uri.startsWith("data:") ? detail.qr_image_data_uri : `data:image/png;base64,${detail.qr_image_data_uri}`}
                  alt="QR PIX"
                  className="w-48 h-48 mx-auto bg-white p-2 rounded"
                />
              )}
              {detail.pix_copy_paste && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">Copia e Cola</div>
                  <div className="text-xs font-mono-jb break-all text-slate-300 border border-slate-800 rounded p-2 bg-slate-900">
                    {detail.pix_copy_paste}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
